'use client';

import { useState } from 'react';
import { ShiftTimeMapping } from '@/lib/types';

interface Props {
  unknownCodes: string[];
  customMappings: ShiftTimeMapping[];
  onUpdateMappings: (mappings: ShiftTimeMapping[]) => void;
}

export default function ShiftTimeConfig({
  unknownCodes,
  customMappings,
  onUpdateMappings,
}: Props) {
  const [editing, setEditing] = useState<Record<string, { startTime: string; endTime: string; isOvernight: boolean }>>(
    Object.fromEntries(
      unknownCodes.map(code => [
        code,
        { startTime: '0800', endTime: '1730', isOvernight: false },
      ])
    )
  );

  if (unknownCodes.length === 0) {
    return (
      <div className="text-sm text-emerald-600 dark:text-emerald-400">
        All shift codes have known time mappings.
      </div>
    );
  }

  return (
    <div className="w-full">
      <h3 className="font-medium text-zinc-800 dark:text-zinc-200 mb-2">
        Configure unknown shift codes
      </h3>
      <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-4">
        These codes were found but don&apos;t have predefined times. Set start and end times for each.
      </p>
      <div className="space-y-3">
        {unknownCodes.map(code => {
          const edit = editing[code] || { startTime: '0800', endTime: '1730', isOvernight: false };
          return (
            <div key={code} className="flex items-center gap-3">
              <span className="font-mono text-sm w-32 text-zinc-800 dark:text-zinc-200">{code}</span>
              <input
                type="text"
                value={edit.startTime}
                onChange={e => setEditing(prev => ({ ...prev, [code]: { ...edit, startTime: e.target.value } }))}
                className="w-24 px-2 py-1 border border-zinc-300 dark:border-zinc-700 rounded bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 text-sm"
                placeholder="0800"
              />
              <span className="text-zinc-500">to</span>
              <input
                type="text"
                value={edit.endTime}
                onChange={e => setEditing(prev => ({ ...prev, [code]: { ...edit, endTime: e.target.value } }))}
                className="w-24 px-2 py-1 border border-zinc-300 dark:border-zinc-700 rounded bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 text-sm"
                placeholder="1730"
              />
              <label className="flex items-center gap-1 text-sm text-zinc-600 dark:text-zinc-400">
                <input
                  type="checkbox"
                  checked={edit.isOvernight}
                  onChange={e => setEditing(prev => ({ ...prev, [code]: { ...edit, isOvernight: e.target.checked } }))}
                />
                Overnight
              </label>
            </div>
          );
        })}
      </div>
      <button
        onClick={() => {
          const newMappings: ShiftTimeMapping[] = Object.entries(editing).map(([code, vals]) => ({
            code,
            startTime: vals.startTime,
            endTime: vals.endTime,
            isOvernight: vals.isOvernight,
            label: code,
          }));
          onUpdateMappings([...customMappings, ...newMappings]);
        }}
        className="mt-4 px-4 py-2 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-md text-sm font-medium hover:bg-zinc-700 dark:hover:bg-zinc-300 transition-colors"
      >
        Apply mappings
      </button>
    </div>
  );
}